import React, { useRef, useState } from "react";
import { Mic } from "lucide-react";
import { api } from "../lib/api";
import { useVoiceRecorder } from "../lib/useVoiceRecorder";
import { getVoice } from "../lib/voicePref";
import { toast } from "sonner";

const b64ToBlob = (b64, mime = "audio/mpeg") => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// Floating quick-ask mic — sits next to the + CAPTURE button.
const AskFAB = () => {
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null);
  const audioRef = useRef(null);

  const onBlob = async (blob) => {
    setBusy(true);
    setReply(null);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      fd.append("audio", blob, `ask.${ext}`);
      fd.append("speak", "true");
      fd.append("voice", getVoice());
      const res = await api.assistantVoice(fd);
      setReply({ transcript: res.transcript, text: res.reply });
      if (res.audio_b64 && audioRef.current) {
        audioRef.current.src = URL.createObjectURL(b64ToBlob(res.audio_b64));
        audioRef.current.play().catch(() => {});
      }
      // Auto-clear the bubble after a while
      setTimeout(() => setReply(null), 20000);
    } catch (e) {
      toast("Ask failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  const voice = useVoiceRecorder(onBlob);

  return (
    <>
      <button
        onMouseDown={voice.start}
        onMouseUp={voice.stop}
        onMouseLeave={() => { if (voice.state === "recording") voice.stop(); }}
        onTouchStart={(e) => { e.preventDefault(); voice.start(); }}
        onTouchEnd={(e) => { e.preventDefault(); voice.stop(); }}
        disabled={busy}
        data-testid="ask-fab"
        data-recording={voice.state === "recording"}
        aria-label="Hold to ask Operator"
        className={`fixed bottom-24 md:bottom-8 right-[172px] md:right-[196px] z-40 flex items-center gap-2 pl-3 pr-4 py-3 border transition-all select-none ${
          voice.state === "recording"
            ? "bg-[rgba(239,68,68,0.15)] border-[#ef4444] text-[#ef4444] op-p1-flash"
            : "bg-[#0A0C14]/90 backdrop-blur-md border-[rgba(0,229,255,0.35)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.08)]"
        }`}
      >
        <Mic size={14} />
        <span className="op-mono text-[10px] tracking-[0.24em]">
          {voice.state === "recording" ? `ASK · ${String(voice.elapsed).padStart(2, "0")}s` : busy ? "…" : "ASK"}
        </span>
      </button>

      {reply && (
        <div
          data-testid="ask-fab-reply"
          className="fixed bottom-[152px] md:bottom-[80px] right-6 z-40 max-w-sm op-panel op-panel--glow p-4"
        >
          <div className="op-tag mb-1 text-[#00E5FF]">OPERATOR</div>
          <div className="text-white text-sm leading-relaxed">{reply.text}</div>
          {reply.transcript && (
            <div className="op-tag mt-2 text-[#5E657D]">YOU: "{reply.transcript}"</div>
          )}
        </div>
      )}
      <audio ref={audioRef} className="hidden" data-testid="ask-fab-audio" />
    </>
  );
};

export default AskFAB;
